import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Minus, Gift, ArrowLeft, RotateCcw } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuth";
import { adminApi, API } from "../lib/api";
import { LOGO_URL } from "../data/content";

export default function LoyaltyScan() {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const { admin, checking } = useAdminAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (checking) return;
    // Public users get redirected to their card view
    if (!admin) {
      const owned = localStorage.getItem("amyu_loyalty_card_id");
      if (owned === cardId) {
        navigate("/carte-fidelite", { replace: true });
      } else {
        // Not admin, not owner — go to signup page (or login)
        navigate("/admin/login", { replace: true, state: { from: `/s/${cardId}` } });
      }
      return;
    }
    adminApi
      .get(`/admin/loyalty/${cardId}`)
      .then((r) => setCard(r.data.card))
      .catch((err) =>
        toast.error(err.response?.data?.detail || "Carte introuvable")
      )
      .finally(() => setLoading(false));
  }, [admin, checking, cardId, navigate]);

  const act = async (action) => {
    setActing(true);
    try {
      const r = await adminApi.post(`/admin/loyalty/${cardId}/action`, { action });
      setCard(r.data.card);
      const labels = {
        stamp: "+1 tampon ajouté ♡",
        unstamp: "−1 tampon retiré",
        redeem: "🎉 Récompense enregistrée !",
        reset: "Carte réinitialisée",
      };
      toast.success(labels[action] || "Action effectuée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action refusée");
    } finally {
      setActing(false);
    }
  };

  if (checking || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amyu-bg">
        <Loader2 className="animate-spin text-coral" size={32} />
      </div>
    );
  }
  if (!card) return null;

  const threshold = card.reward_threshold || 10;
  const canRedeem = card.stamps >= threshold;

  return (
    <div className="min-h-screen bg-amyu-bg py-8 px-5">
      <div className="max-w-md mx-auto">
        <a
          href="/admin/loyalty"
          className="inline-flex items-center gap-2 text-sm text-amyu-muted hover:text-coral mb-6"
        >
          <ArrowLeft size={14} /> Toutes les cartes
        </a>

        <div className="bg-white rounded-[28px] p-6 shadow-lg" data-testid="loyalty-scan-card">
          <div className="flex items-center justify-between">
            <img src={LOGO_URL} alt="Amyu" className="h-10" />
            <span className="text-[10px] tracking-[0.24em] uppercase text-coral font-medium">
              Scan admin
            </span>
          </div>

          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-amyu-muted">
              Client
            </div>
            <div className="font-display text-2xl mt-1">{card.name}</div>
            <div className="text-xs text-amyu-muted">{card.email}</div>
          </div>

          <div className="mt-6 bg-amyu-pink/60 rounded-2xl p-5 text-center">
            <div className="text-[11px] uppercase tracking-[0.2em] text-coral">
              Progression
            </div>
            <div className="font-display text-5xl text-coral mt-1">
              {card.stamps}
              <span className="text-2xl text-amyu-muted">/{threshold}</span>
            </div>
            <div className="text-xs text-amyu-muted mt-1">
              {canRedeem
                ? "🎉 Récompense disponible !"
                : `Encore ${threshold - card.stamps} avant récompense`}
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <button
              onClick={() => act("stamp")}
              disabled={acting}
              data-testid="loyalty-scan-stamp"
              className="w-full bg-coral text-white rounded-full py-3.5 font-medium flex items-center justify-center gap-2 hover:bg-coral/90 transition-all disabled:opacity-60"
            >
              <Plus size={16} /> +1 tampon
            </button>
            <button
              onClick={() => act("redeem")}
              disabled={acting || !canRedeem}
              data-testid="loyalty-scan-redeem"
              className={`w-full rounded-full py-3.5 font-medium flex items-center justify-center gap-2 transition-all ${
                canRedeem
                  ? "bg-amyu-mango text-amyu-ink hover:brightness-95"
                  : "bg-amyu-cream text-amyu-muted cursor-not-allowed"
              }`}
            >
              <Gift size={16} /> Utiliser récompense (−{threshold})
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => act("unstamp")}
                disabled={acting || card.stamps <= 0}
                data-testid="loyalty-scan-unstamp"
                className="bg-amyu-cream text-amyu-ink rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-2 hover:bg-amyu-cream/70 disabled:opacity-40"
              >
                <Minus size={14} /> −1
              </button>
              <button
                onClick={() => {
                  if (window.confirm("Réinitialiser tous les tampons de cette carte ?")) act("reset");
                }}
                disabled={acting || card.stamps <= 0}
                data-testid="loyalty-scan-reset"
                className="bg-amyu-cream text-amyu-ink rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-2 hover:bg-amyu-cream/70 disabled:opacity-40"
              >
                <RotateCcw size={14} /> Reset
              </button>
            </div>
          </div>

          <div className="mt-6 pt-5 border-t border-amyu/30">
            <div className="text-[11px] uppercase tracking-[0.2em] text-amyu-muted mb-2">
              Historique récent
            </div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto text-xs">
              {[...(card.history || [])].reverse().slice(0, 12).map((h) => (
                <div key={h.at + h.action} className="flex justify-between text-amyu-muted">
                  <span className="capitalize">
                    {h.action}
                    {h.delta ? ` (${h.delta > 0 ? "+" : ""}${h.delta})` : ""}
                  </span>
                  <span>
                    {new Date(h.at).toLocaleDateString("fr-FR", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
